import React from 'react'

import { isFormattedData } from 'utils/widgetData'

/* Organisms */
import Errors from 'components_new/organisms/Widget/Errors'
import FetchError from 'components_new/organisms/Widget/Errors/FetchError'
import Image from 'components_new/organisms/Widget/Types/Image'
import Text from 'components_new/organisms/Widget/Types/Text'
import Types from 'components_new/organisms/Widget/Types'

import Loading from './loading'

/* Hooks */
import { useWidgetFetchError } from 'redux/hooks/Widgets'

/* Types */
import { DashboardVariantType } from 'types/GlobalDashboard'
import { DashboardFilter } from 'types/GlobalDashboardFilter'
import {
  CustomPeriodFilter,
  FormattedWidgetData,
  InnerWidgetPatchBody,
  PeriodFilter,
  ParsedSegmentPath,
  TViewport,
  WidgetOrder,
  WidgetType,
  TCoreWidget,
  isCoreWidgetFullyLoaded
} from 'types/GlobalWidget'

interface WidgetBodyProps {
  customSegmentBy: string | null
  dashboardFilter: DashboardFilter
  editMode: boolean

  // Override real loading, used for map.
  fakeLoading: boolean
  fetchWidgetData?: (widget: TCoreWidget) => void
  fullSize: boolean
  embedded: boolean
  formattedData: FormattedWidgetData | null
  interactive: boolean
  isAdmin: boolean
  order: WidgetOrder
  periodFilter: PeriodFilter | null
  scaleFactor: number
  updateWidget: (id: string, body: InnerWidgetPatchBody) => void
  setCustomSegmentBy: (
    attributeOptionId: string,
    dbFilter: DashboardFilter,
    periodFilter?: CustomPeriodFilter
  ) => void
  setDashboardFilter: (
    dbFilter: DashboardFilter,
    periodFilter?: CustomPeriodFilter
  ) => void
  setEdit: (arg0: boolean) => void
  setOrder: (order: WidgetOrder) => void
  setViewport: (value: TViewport) => void
  standaloneWidget: boolean
  variantType: DashboardVariantType
  viewport: TViewport
  widget: TCoreWidget
  showUnderlyingContent: (
    segmentPaths: ParsedSegmentPath[],
    initialKpiOptionId: string | null
  ) => void
}

const WidgetBody = (props: WidgetBodyProps) => {
  const {
    customSegmentBy,
    dashboardFilter,
    editMode,
    embedded,
    fakeLoading,
    fetchWidgetData,
    formattedData,
    fullSize,
    interactive,
    isAdmin,
    order,
    periodFilter,
    scaleFactor,
    setCustomSegmentBy,
    setDashboardFilter,
    setEdit,
    setOrder,
    setViewport,
    showUnderlyingContent,
    standaloneWidget,
    updateWidget,
    variantType,
    viewport,
    widget
  } = props

  const fetchError = useWidgetFetchError(widget.id)

  // Handle TEXT widget early - it doesn't have settings or data
  if (widget.type === WidgetType.TEXT) {
    return (
      <Text
        editMode={editMode}
        scaleFactor={scaleFactor}
        updateWidget={updateWidget}
        widget={widget}
      />
    )
  }

  // Handle IMAGE widget early - it doesn't have settings or data
  if (widget.type === WidgetType.IMAGE) {
    return <Image setEdit={setEdit} widget={widget} />
  }

  // Check for fetch errors (HTTP errors thrown during data fetch)
  if (fetchError !== undefined) {
    return (
      <FetchError
        error={fetchError}
        fetchWidgetData={fetchWidgetData}
        scaleFactor={scaleFactor}
        widget={widget}
      />
    )
  }

  // Check for widget status errors (backend-provided error states)
  if (widget.status && widget.status.broken) {
    return (
      <Errors
        embedded={embedded}
        fetchWidgetData={fetchWidgetData}
        isAdmin={isAdmin}
        onOpenSettings={() => setEdit(true)}
        scaleFactor={scaleFactor}
        errorStatus={widget.status}
        widget={widget}
      />
    )
  }

  if (
    !isFormattedData(formattedData) ||
    !isCoreWidgetFullyLoaded(widget) ||
    fakeLoading
  ) {
    return <Loading widget={widget} scaleFactor={scaleFactor} />
  }

  return (
    <Types
      customSegmentBy={customSegmentBy}
      dashboardFilter={dashboardFilter}
      fullSize={fullSize}
      embedded={embedded}
      formattedData={formattedData}
      interactive={interactive}
      isAdmin={isAdmin}
      order={order}
      periodFilter={periodFilter}
      scaleFactor={scaleFactor}
      setCustomSegmentBy={setCustomSegmentBy}
      setDashboardFilter={setDashboardFilter}
      setOrder={setOrder}
      setViewport={setViewport}
      standaloneWidget={standaloneWidget}
      variantType={variantType}
      viewport={viewport}
      widget={widget}
      showUnderlyingContent={showUnderlyingContent}
    />
  )
}

export default WidgetBody
